// Reactions of rows no message store holds: the agent chat's prompts and replies (DM ledger
// rows drawn off the agent timeline) and DefaultSessionView's own list. app.svelte.ts's
// patchReactions writes here like the channel/DM stores, so live frames and toggleReaction
// land on every TRACKED row — one the backfill, the send's ack or the post-persist DM frame
// proved exists. ponytail: never pruned; prune per workspace if it ever shows up in memory.
import { reportUiFailure } from "$lib/report-failure.js";
// The leaf singleton, not app.svelte.ts: every agent surface imports this module.
import { client } from "$lib/state/client.js";
import type { Reaction } from "$lib/types.js";
import { normalizeReactions } from "./reactions.js";

// One read carries at most this many ids (the reactions endpoint's cap).
const READ_CAP = 200;
// A row an on-demand read found absent is not asked again on hover for this long (or until a refresh).
export const ABSENT_RETRY_MS = 5_000;

class RowReactions {
  #rows = $state<Record<string, Reaction[]>>({});
  // On-demand reads (ensure): ids queued per workspace for the next tick, and ids being read.
  #queued = new Map<string, Set<string>>();
  #reading = new Set<string>();
  // Read and absent (ensure): id -> when. ponytail: never pruned, like #rows.
  #absent = new Map<string, number>();

  known(id: string | null | undefined): boolean {
    return !!id && id in this.#rows;
  }

  get(id: string | null | undefined): Reaction[] {
    return (id && this.#rows[id]) || [];
  }

  // A server read of the row (backfill) REPLACES what is tracked for it.
  set(id: string, reactions: unknown): void {
    this.#rows[id] = normalizeReactions(reactions) ?? [];
  }

  track(id: string): void {
    if (!(id in this.#rows)) this.#rows[id] = [];
  }

  patch(id: string, next: (reactions: Reaction[]) => Reaction[]): void {
    const current = this.#rows[id];
    if (current) this.#rows[id] = next(current);
  }

  // Reload / reconnect / turn end / rows newly on screen. A failed read leaves what is shown untouched.
  // It re-opens hover reads for the ids it asks (a row may be written by now). False: the read failed.
  async refresh(workspaceId: string, ids: readonly string[]): Promise<boolean> {
    if (ids.length === 0) return true;
    for (const id of ids) this.#absent.delete(id);
    try {
      const got = await client.fetchMessageReactions(workspaceId, [...ids]);
      for (const [id, reactions] of Object.entries(got)) this.set(id, reactions);
      return true;
    } catch (err) {
      reportUiFailure("reactions.refresh", err, { workspaceId });
      return false;
    }
  }

  // A row not known yet, read when the user reaches for it (hover, focus): the ids asked for in one
  // tick go out together, each read once at a time; a row the server did not return is asked again
  // only after ABSENT_RETRY_MS or a refresh (the turn-end read), never on every hover.
  ensure(workspaceId: string, ids: readonly string[]): void {
    const now = Date.now();
    const absent = (id: string) => now - (this.#absent.get(id) ?? -Infinity) < ABSENT_RETRY_MS;
    const fresh = ids.filter((id) => !this.known(id) && !this.#reading.has(id) && !absent(id));
    if (fresh.length === 0) return;
    let queue = this.#queued.get(workspaceId);
    if (!queue) {
      queue = new Set();
      this.#queued.set(workspaceId, queue);
      queueMicrotask(() => void this.#readQueued(workspaceId));
    }
    for (const id of fresh) queue.add(id);
  }

  // A live reaction proves the row exists: its "absent" memo no longer applies.
  ensureExisting(workspaceId: string, ids: readonly string[]): void {
    for (const id of ids) this.#absent.delete(id);
    this.ensure(workspaceId, ids);
  }

  async #readQueued(workspaceId: string): Promise<void> {
    const ids = [...(this.#queued.get(workspaceId) ?? [])];
    this.#queued.delete(workspaceId);
    for (const id of ids) this.#reading.add(id);
    for (let i = 0; i < ids.length; i += READ_CAP) {
      const batch = ids.slice(i, i + READ_CAP);
      const read = await this.refresh(workspaceId, batch);
      for (const id of batch) {
        this.#reading.delete(id);
        if (read && !this.known(id)) this.#absent.set(id, Date.now()); // a failed read is not "absent"
      }
    }
  }
}

export const rowReactions = new RowReactions();
